// src/context/AuthContext.js
import React, {
    createContext, useState, useContext,
    useMemo, useCallback, useEffect, useRef
} from 'react';
import api from '../services/api';
import socket from '../services/socket'; // 🛡️ FIX M1: gobernamos el ciclo de vida del socket

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    // Inicialización lazy desde localStorage para evitar parpadeo en recargas
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || null;
        } catch {
            return null;
        }
    });

    // FIX: loading es necesario para que App.js no redirija a /login
    // mientras /auth/me está verificando la cookie silenciosamente
    const [loading, setLoading] = useState(true);

    // Ref para evitar doble verificación en React.StrictMode
    const verificadoRef = useRef(false);

    // =========================================================================
    // VERIFICACIÓN SILENCIOSA DE SESIÓN AL ARRANCAR
    // Confirma con el backend que la cookie sigue siendo válida.
    // Si /auth/me falla → limpiamos localStorage y forzamos re-login.
    // =========================================================================
    useEffect(() => {
        if (verificadoRef.current) return;
        verificadoRef.current = true;

        const verificarSesion = async () => {
            try {
                const res = await api.get('/auth/me');
                // El interceptor de api.js devuelve payload directo: { success, user }
                const usuarioVerificado = res?.user || res;
                if (usuarioVerificado?.id) {
                    // Actualizamos con los datos frescos del servidor
                    localStorage.setItem('user', JSON.stringify(usuarioVerificado));
                    setUser(usuarioVerificado);
                    if (!socket.connected) socket.connect(); // ✅ sesión válida → conectamos
                } else {
                    // La cookie existe pero el servidor no reconoce el usuario
                    localStorage.removeItem('user');
                    setUser(null);
                }
            } catch {
                // Cookie inválida o expirada → purgar sesión
                localStorage.removeItem('user');
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        verificarSesion();
    }, []);

    // =========================================================================
    // ESCUCHA DE EVENTOS DE SESIÓN EXPIRADA (disparado por el interceptor de api.js)
    // =========================================================================
    useEffect(() => {
        const handleSesionExpirada = () => {
            socket.disconnect(); // 🛡️ FIX M1: cortamos el socket al expirar la sesión
            localStorage.removeItem('user');
            setUser(null);
        };

        window.addEventListener('auth_session_expired', handleSesionExpirada);
        return () => window.removeEventListener('auth_session_expired', handleSesionExpirada);
    }, []);

    const loginUser = useCallback((userData) => {
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        if (!socket.connected) socket.connect(); // 🛡️ FIX M1: conectamos con la cookie fresca
    }, []);

    const logoutUser = useCallback(async () => {
        try {
            await api.post('/auth/logout');
        } catch {
            // Ignoramos el error de red — de todas formas limpiamos la sesión local
        } finally {
            socket.disconnect(); // 🛡️ FIX M1: matamos la identidad del socket al salir
            localStorage.removeItem('user');
            setUser(null);
        }
    }, []);

    // Recarga los datos del usuario desde el backend (usado tras editar el perfil).
    // FIX: usePerfil.js llamaba a reloadUser pero el contexto no lo exponía → crash silencioso.
    const reloadUser = useCallback(async () => {
        try {
            const res = await api.get('/auth/me');
            const usuarioFresco = res?.user || res;
            if (usuarioFresco?.id) {
                localStorage.setItem('user', JSON.stringify(usuarioFresco));
                setUser(usuarioFresco);
            }
        } catch {
            // Si /auth/me falla, el interceptor global ya gestiona la sesión expirada
        }
    }, []);

    const authValues = useMemo(() => {
        const userRole = user?.role || user?.rol || 'medico';
        const esSuperMedico = userRole === 'supermedico';
        const esAdmin = userRole === 'admin';
        // FIX RBAC: 'admin' incluido en la visión global (facturación, fichajes, reportes)
        const tieneAccesoGlobal = ['supermedico', 'recepcion', 'admin'].includes(userRole);
        // RGPD: recepción ve agenda y datos administrativos, no la historia clínica.
        const veHistoriaClinica = userRole !== 'recepcion';

        return {
            user,
            loading,
            esSuperMedico,
            esAdmin,
            tieneAccesoGlobal,
            veHistoriaClinica,
            login: loginUser,
            logout: logoutUser,
            reloadUser
        };
    }, [user, loading, loginUser, logoutUser, reloadUser]);

    return (
        <AuthContext.Provider value={authValues}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de un <AuthProvider />');
    }
    return context;
};